import { useEffect, useState } from 'react';
import { Button } from '../ui/Button';
import { createCopyRequest, newIdempotencyKey } from '../../api/originatorApi';
import {
  TRANSACTION_TYPES,
  facetLabels,
  initiateTransfer,
  listTransferTypes,
} from '../../api/transferApi';
import { ASSET_NOT_IN_CUSTODY_MESSAGE, formatCustodyFailure } from '../../api/apiError';

/**
 * Request types offered to the Originator.
 * COPY_REQUEST is a workflow ticket; the rest are transfer-engine transactions
 * (BRD EV-17..EV-22) that lock the authority version at instruction time.
 */
export const REQUEST_TYPES = [
  { value: 'COPY_REQUEST', label: 'Copy request', kind: 'ticket' },
  ...TRANSACTION_TYPES.map((type) => ({ ...type, kind: 'transfer' })),
];

/** Fallback "what changes / what stays" when the catalogue has not loaded. */
const LOCAL_CATALOGUE = {
  CUSTODY: { changes: ['custodian_label', 'held_for_label', 'logical_account'], requires_counterparty: true },
  CONTROL: { changes: ['control_label'], requires_counterparty: true },
  ASSIGNMENT: { changes: ['owner_label', 'control_label'], requires_counterparty: true },
  PLEDGE: { changes: ['pledgee_label', 'pledged'], requires_counterparty: true },
  RELEASE: { changes: ['pledgee_label', 'pledged'], requires_counterparty: false },
};
const ALL_FACETS = ['owner_label', 'control_label', 'custodian_label', 'held_for_label', 'pledgee_label', 'logical_account', 'pledged'];

const COUNTERPARTY_LABEL = {
  CUSTODY: 'New custodian',
  CONTROL: 'New control party',
  ASSIGNMENT: 'Assignee (new owner)',
  PLEDGE: 'Pledgee',
};

const inputClass =
  'w-full rounded-evault border border-evault-select-ring/70 bg-white px-3 py-2 text-sm text-umb-neutral-dark shadow-sm focus:border-evault-teal focus:outline-none focus:ring-2 focus:ring-evault-teal/20';

function catalogueFor(catalogue, type) {
  const fromServer = catalogue.find((entry) => entry.transaction_type === type);
  if (fromServer) return fromServer;
  const local = LOCAL_CATALOGUE[type];
  if (!local) return null;
  return {
    transaction_type: type,
    supported: true,
    changes: local.changes,
    unchanged: ALL_FACETS.filter((key) => !local.changes.includes(key)),
    requires_counterparty: local.requires_counterparty,
    extra_fields: [],
  };
}

/**
 * Create a copy request or instruct a transfer transaction. The idempotency
 * key is generated once per submit intent (when the modal opens) and reused
 * across retries/double-clicks so the backend can dedupe.
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSubmitted?: (response: object) => void,
 *   assetId?: string,
 *   lockAsset?: boolean,
 *   defaultType?: string,
 *   custodyState?: string | null,
 * }} props
 *
 * `custodyState` is the asset's `custody_state` when the caller already has it.
 * Requests act on the custodian's record, so anything other than ACCEPTED is
 * refused by the backend (409 ASSET_NOT_IN_CUSTODY); we surface that up-front.
 */
export function CreateRequestModal({
  open,
  onClose,
  onSubmitted,
  assetId: presetAssetId,
  lockAsset = false,
  defaultType = 'COPY_REQUEST',
  custodyState = null,
}) {
  const [assetId, setAssetId] = useState(presetAssetId || '');
  const [requestType, setRequestType] = useState(defaultType);
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [counterparty, setCounterparty] = useState('');
  const [alsoControl, setAlsoControl] = useState(true);
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [catalogue, setCatalogue] = useState([]);

  // Reset form state and mint a fresh idempotency key each time the modal opens.
  useEffect(() => {
    if (open) {
      setAssetId(presetAssetId || '');
      setRequestType(defaultType);
      setNotes('');
      setReason('');
      setCounterparty('');
      setAlsoControl(true);
      setError('');
      setSuccess('');
      setIdempotencyKey(newIdempotencyKey());
    }
  }, [open, presetAssetId, defaultType]);

  // The server catalogue is the source of truth for what a type may change.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => listTransferTypes())
      .then((types) => {
        if (!cancelled && Array.isArray(types)) setCatalogue(types);
      })
      .catch(() => {
        // Local fallback keeps the form usable; the backend still enforces the whitelist.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const selected = REQUEST_TYPES.find((type) => type.value === requestType) ?? REQUEST_TYPES[0];
  const isTransfer = selected.kind === 'transfer';
  const info = isTransfer ? catalogueFor(catalogue, selected.value) : null;
  const needsCounterparty = Boolean(info?.requires_counterparty);
  const notInCustody = custodyState != null && custodyState !== 'ACCEPTED';

  async function handleSubmit(event) {
    event.preventDefault();
    if (!assetId.trim()) {
      setError('Asset ID is required.');
      return;
    }
    if (notInCustody) {
      setError(ASSET_NOT_IN_CUSTODY_MESSAGE);
      return;
    }
    if (isTransfer && needsCounterparty && !counterparty.trim()) {
      setError(`${COUNTERPARTY_LABEL[selected.value] || 'Counterparty'} is required.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let response;
      if (isTransfer) {
        const changes = selected.value === 'ASSIGNMENT' ? { also_control: alsoControl } : {};
        response = await initiateTransfer(
          assetId.trim(),
          {
            transaction_type: selected.value,
            counterparty_label: needsCounterparty ? counterparty.trim() : null,
            changes,
            notes: notes.trim() || null,
          },
          idempotencyKey
        );
        setSuccess(
          `Transfer instructed and locked to authority version ${
            response?.expected_authority_version ?? '—'
          }. The custodian will accept and commit it.`
        );
      } else {
        response = await createCopyRequest(assetId.trim(), { notes: reason.trim() || null }, idempotencyKey);
        setSuccess('Request submitted.');
      }
      if (onSubmitted) onSubmitted(response);
    } catch (requestError) {
      setError(formatCustodyFailure(requestError, 'Unable to submit request.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-request-heading"
    >
      <div className="w-full max-w-md rounded-evault border border-evault-border bg-white p-5 shadow-xl sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <h2 id="create-request-heading" className="text-base font-semibold text-umb-neutral-dark">
            New custody request
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-2 py-1 text-sm text-umb-neutral-dark/60 hover:bg-evault-surface"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <p className="mt-1 text-xs text-evault-muted">
          {isTransfer
            ? 'Instructs a transfer on the custodian’s authority record. Nothing changes until the custodian commits it.'
            : 'Creates a workflow request routed to the custodian. It does not execute the action.'}
        </p>
        {notInCustody ? (
          <p
            className="mt-3 rounded-evault border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            role="note"
            data-testid="request-not-in-custody"
          >
            {ASSET_NOT_IN_CUSTODY_MESSAGE}
          </p>
        ) : null}

        {success ? (
          <div className="mt-5 space-y-4">
            <p className="rounded-evault border border-umb-success/20 bg-umb-success/[0.06] px-3 py-3 text-sm font-medium text-umb-success" role="status">
              {success}
            </p>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form className="mt-5 space-y-4" onSubmit={handleSubmit} noValidate>
            <label className="block">
              <span className="text-xs font-medium text-umb-neutral-dark/70">Asset ID</span>
              <input
                className={`${inputClass} mt-1 ${lockAsset ? 'bg-evault-surface/60' : ''}`}
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
                placeholder="Asset UUID"
                readOnly={lockAsset}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-umb-neutral-dark/70">Request type</span>
              <select
                className={`${inputClass} mt-1`}
                value={requestType}
                onChange={(event) => setRequestType(event.target.value)}
              >
                {REQUEST_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </label>

            {isTransfer && info ? (
              <div
                className="rounded-evault border border-evault-border bg-evault-surface/50 px-3 py-2 text-xs"
                data-testid="transfer-type-effects"
              >
                {selected.hint ? <p className="text-umb-neutral-dark/85">{selected.hint}.</p> : null}
                <p className="mt-1">
                  <span className="font-semibold text-umb-neutral-dark">Changes:</span>{' '}
                  {facetLabels(info.changes).join(', ') || 'nothing'}
                </p>
                <p className="mt-0.5">
                  <span className="font-semibold text-umb-neutral-dark">Stays unchanged:</span>{' '}
                  {facetLabels(info.unchanged).join(', ') || '—'}
                </p>
                <p className="mt-1 text-evault-muted">
                  The sealed vault files do not move. Only the authority record changes, and only after the
                  custodian commits.
                </p>
              </div>
            ) : null}

            {isTransfer && needsCounterparty ? (
              <label className="block">
                <span className="text-xs font-medium text-umb-neutral-dark/70">
                  {COUNTERPARTY_LABEL[selected.value] || 'Counterparty'}
                </span>
                <input
                  className={`${inputClass} mt-1`}
                  value={counterparty}
                  onChange={(event) => setCounterparty(event.target.value)}
                  placeholder="Legal name of the party gaining the relationship"
                  maxLength={255}
                />
              </label>
            ) : null}

            {isTransfer && selected.value === 'ASSIGNMENT' ? (
              <label className="flex items-center gap-2 text-xs text-umb-neutral-dark/85">
                <input
                  type="checkbox"
                  checked={alsoControl}
                  onChange={(event) => setAlsoControl(event.target.checked)}
                />
                Also pass control to the assignee (they instruct future transfers)
              </label>
            ) : null}

            {requestType === 'COPY_REQUEST' ? (
              <label className="block">
                <span className="text-xs font-medium text-umb-neutral-dark/70">Reason</span>
                <textarea
                  className={`${inputClass} mt-1`}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Reason for this copy request"
                />
              </label>
            ) : (
              <label className="block">
                <span className="text-xs font-medium text-umb-neutral-dark/70">Notes (optional)</span>
                <textarea
                  className={`${inputClass} mt-1`}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="Add context for the custodian…"
                />
              </label>
            )}

            {error ? (
              <p className="text-sm text-umb-error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || notInCustody}>
                {submitting ? 'Submitting…' : isTransfer ? 'Instruct transfer' : 'Submit request'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
